/**
 * Published Prototype Template (100% Shared)
 * 
 * Platform-agnostic template for displaying published prototypes.
 * Shows prototype in full-screen iframe or error states (loading, 404).
 * 
 * This template is completely shared between web and plugin.
 */

import React from 'react';
import { Button } from '@ideoz/ui';
import { Home, RefreshCw } from 'lucide-react';
import { ApiUrls } from '@ideoz/core';

// ============================================================================
// INTERFACES
// ============================================================================

export interface PublishedResourceViewProps {
  /** Loading state */
  isLoading: boolean;

  /** Error message if any */
  error: string | null;

  /** Prototype demo URL to display in iframe */
  demoUrl: string | null;

  /** Page title (from prototype header) */
  title: string;

  /** Use artifact copy / labels (published drawing HTML) vs prototype */
  variant?: 'prototype' | 'artifact';

  /** Navigate to home action */
  onNavigateHome: () => void;

  /** Retry loading action */
  onRetry: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PublishedResourceView({
  isLoading,
  error,
  demoUrl,
  title,
  variant = 'prototype',
  onNavigateHome,
  onRetry,
}: PublishedResourceViewProps) {
  const isArtifact = variant === 'artifact';

  // Loading state
  if (isLoading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex flex-col items-center gap-6">
          <img
            src="https://files.ideoz.ai/prototyperLoading.gif"
            alt={isArtifact ? 'Loading artifact' : 'Loading prototype'}
            className="w-64 h-64 object-contain"
          />
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {isArtifact ? 'Loading Artifact' : 'Loading Prototype'}
            </h2>
            <p className="text-base text-gray-600">
              {isArtifact
                ? 'Please wait while we prepare your artifact...'
                : 'Please wait while we prepare your prototype...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state (404 or other errors)
  if (error || !demoUrl) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
        <div className="max-w-md w-full px-6 py-12 text-center">
          {/* Error Icon */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="absolute -top-1 -right-1 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">!</span>
              </div>
            </div>
          </div>
          {/* Error Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            {isArtifact ? 'Artifact Not Found' : 'Prototype Not Found'}
          </h1>
          <p className="text-gray-600 mb-8 leading-relaxed">
            {isArtifact
              ? 'The artifact you are looking for does not exist or has been removed.'
              : 'The prototype you are looking for does not exist or has been removed.'}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={onNavigateHome}
              className="flex items-center justify-center gap-2 bg-primary-900 hover:bg-primary-800 text-white px-6 py-3 rounded-lg transition-colors"
            >
              <Home className="w-4 h-4" />
              Go to Home
            </Button>

            <Button
              onClick={onRetry}
              variant="outline"
              className="flex items-center justify-center gap-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }
  // Success state - render iframe
  return (
    <div className="w-screen h-screen overflow-hidden relative">
      <iframe
        src={demoUrl}
        className="w-full h-full border-0"
        title={title || (isArtifact ? 'Published Artifact' : 'Published Prototype')}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="fullscreen; clipboard-write"
        loading="eager"
      />

      {/* Floating "Generated by Ideoz" button */}
      <a
        href={ApiUrls.platformLink}
        className="fixed bottom-4 right-4 bg-primary-900 hover:bg-primary-800 text-white text-sm font-medium px-4 py-2 rounded-full shadow-lg transition-all hover:shadow-xl flex items-center gap-2 z-50"
      >
        {/* <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg> */}
        Generated by Ideoz
      </a>
    </div>
  );
}

export default PublishedResourceView;
